import { useMemo, useEffect, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { get } from "lodash";
import useAuth from "hooks/useAuth";
import useApp from "hooks/useApp";
import usePostForm from "hooks/usePostForm";
import IndexLayout from "layouts/IndexLayout";
import MainLayout from "layouts/MainLayout";
import AuthRoutes from "components/AuthRoutes";
import IndexPage from "pages/index/IndexPage";
import UsersList from "pages/users/List";

import UsersAdd from "pages/users/Add";
import ModulesList from "pages/modules/List";
import ModulesView from "pages/modules/View";
import ModulesAdd from "pages/modules/Add";
import ModulesEdit from "pages/modules/Edit";
import ActionsList from "pages/actions/List";
import ActionsView from "pages/actions/View";
import ActionsAdd from "pages/actions/Add";
import ActionsEdit from "pages/actions/Edit";
import MetricsList from "pages/metrics/List";
import MetricsView from "pages/metrics/View";
import MetricsAdd from "pages/metrics/Add";
import MetricsEdit from "pages/metrics/Edit";
import AccountPages from "pages/account";
import UserPages from "pages/users/index";
import HomePage from "./pages/home/homepage/HomePage";
import Dashboard from "./pages/home/dashboard/Dashboard";
import IndexPages from "./pages/index";
import ErrorPages from "./pages/errors";
import "primereact/resources/primereact.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "assets/styles/layout.scss";
import RolesListPage from "pages/roles/List";
import RolesAddPage from "pages/roles/Add";
import RolesViewPage from "pages/roles/View";
import RolesEditPage from "pages/roles/Edit";
import OrgListPage from "pages/org/List";
import OrgAddPage from "pages/org/Add";
import OrgViewPage from "pages/org/View";
import OrgEditPage from "pages/org/Edit";
import CompleteModules from "pages/home/homepage/CompleteModules";

import IngestionApiPage from "pages/api/Ingestion";
import LicensePage from "pages/license/License";
import PatientView from "pages/home/dashboard/PatientView";
import EventsListPage from "pages/events/List";
import EventsViewPage from "pages/events/View";
import EventsEditPage from "pages/events/Edit";
import EventsAddPage from "pages/events/Add";
import Settings from "pages/settings";
const App = () => {
  const auth = useAuth();
  const app = useApp();
  const location = useLocation();
  const previousLocation = useRef(null);
  const roleName = get(auth, "user.roleName", "Super Admin");

  const usersAccountStatus = useMemo(
    () => ["Active", "Verified", "Not Verified", "Pending", "Suspended"],
    []
  );
  const patientType = ["IN", "OUT"];

  const { validateLicense, isLicenseExpired } = usePostForm({});

  useEffect(() => {
    if (previousLocation.current !== location.pathname &&
        location.pathname.indexOf("dashboard") !== -1
    ) {
      validateLicense();
    }
    previousLocation.current = location.pathname;
  }, [location]);

  useEffect(() => {
    if (isLicenseExpired) {
      auth.logout();
    }
  }, [isLicenseExpired]);

  return (
    <Routes>
      <Route exact element={<AuthRoutes />}>
        <Route
          element={
            <MainLayout
              organisations={app.organisations}
              setSelectedOrg={app.setSelectedOrg}
              selectedOrg={app.selectedOrg}
            />
          }
        >
          <Route path="/home" element={<HomePage />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route
            path="/complete_modules"
            element={
              <CompleteModules
                getModulesByOrgURL={app.getModulesByOrgURL}
                selectedOrg={app.selectedOrg}
              />
            }
          />
          <Route path="/dashboard/:moduleId" element={<Dashboard />} />
          <Route path="/patient/:patientId" element={<PatientView />} />
          <Route
            path="/account/*"
            element={
              <AccountPages
                organisations={app.organisations}
                roles={app.roles}
                accountStatus={usersAccountStatus}
              />
            }
          />

          {(roleName === "Super Admin" || roleName === "Admin") && (
            <>
              {/* users pages routes */}
              <Route
                path="/users"
                element={
                  <UsersList
                    selectedOrg={app.selectedOrg}
                    organisations={app.organisations}
                    roles={app.roles}
                  />
                }
              />
              <Route
                path="/users/:fieldName/:fieldValue"
                element={<UsersList />}
              />
              <Route
                path="/users/index/:fieldName/:fieldValue"
                element={<UsersList />}
              />
              <Route
                path="/users/add"
                element={
                  <UsersAdd
                    organisations={app.organisations}
                    roles={app.roles}
                    accountStatus={usersAccountStatus}
                    modules={app.modules}
                  />
                }
              />
              <Route
                path="/users/view/:pageid"
                element={
                  <UserPages
                    organisations={app.organisations}
                    roles={app.roles}
                    accountStatus={usersAccountStatus}
                    modules={app.modules}
                  />
                }
              />
              <Route
                path="/users/edit/:pageid"
                element={
                  <UserPages
                    organisations={app.organisations}
                    roles={app.roles}
                    accountStatus={usersAccountStatus}
                    modules={app.modules}
                  />
                }
              />

              {/* events pages routes */}
              <Route
                path="/events"
                element={<EventsListPage selectedOrg={app.selectedOrg} />}
              />
              <Route
                path="/events/:fieldName/:fieldValue"
                element={<EventsListPage />}
              />
              <Route
                path="/events/index/:fieldName/:fieldValue"
                element={<EventsListPage />}
              />
              <Route
                path="/events/add"
                element={
                  <EventsAddPage
                    organisations={app.organisations}
                    modules={app.modules}
                    actions={app.actions}
                    patientType={patientType}
                  />
                }
              />
              <Route
                path="/events/view/:pageid"
                element={
                  <EventsViewPage
                    organisations={app.organisations}
                    modules={app.modules}
                    patientType={patientType}
                  />
                }
              />
              <Route
                path="/events/edit/:pageid"
                element={
                  <EventsEditPage
                    organisations={app.organisations}
                    modules={app.modules}
                    patientType={patientType}
                  />
                }
              />
              {/* settings page routes */}
              <Route
                path="/settings"
                element={<Settings />}
              />

              {/* org pages routes */}
              <Route path="/org" element={<OrgListPage />} />
              <Route
                path="/org/:fieldName/:fieldValue"
                element={<OrgListPage />}
              />
              <Route
                path="/org/index/:fieldName/:fieldValue"
                element={<OrgListPage />}
              />
              <Route path="/org/add" element={<OrgAddPage />} />
              <Route path="/org/view/:pageid" element={<OrgViewPage />} />
              <Route path="/org/edit/:pageid" element={<OrgEditPage />} />

              {/* modules pages routes */}
              <Route
                path="/modules"
                element={
                  <ModulesList
                    organisations={app.organisations}
                    selectedOrg={app.selectedOrg}
                  />
                }
              />
              <Route
                path="/modules/:fieldName/:fieldValue"
                element={<ModulesList organisations={app.organisations} />}
              />
              <Route
                path="/modules/index/:fieldName/:fieldValue"
                element={<ModulesList organisations={app.organisations} />}
              />
              <Route
                path="/modules/view/:pageid"
                element={<ModulesView organisations={app.organisations} />}
              />
              <Route path="/api/ingestion" element={<IngestionApiPage />} />
              <Route path="/license" element={<LicensePage />} />
              {roleName === "Super Admin" && (
                <>
                  <Route
                    path="/modules/add"
                    element={<ModulesAdd organisations={app.organisations} />}
                  />
                  <Route
                    path="/modules/edit/:pageid"
                    element={<ModulesEdit organisations={app.organisations} />}
                  />
                </>
              )}

              {/* roles pages routes */}
              <Route path="/roles" element={<RolesListPage />} />
              <Route
                path="/roles/:fieldName/:fieldValue"
                element={<RolesListPage />}
              />
              <Route
                path="/roles/index/:fieldName/:fieldValue"
                element={<RolesListPage />}
              />
              <Route path="/roles/add" element={<RolesAddPage />} />
              <Route path="/roles/view/:pageid" element={<RolesViewPage />} />
              <Route path="/roles/edit/:pageid" element={<RolesEditPage />} />
            </>
          )}
          {roleName === "Super Admin" && (
            <>
              {/* actions pages routes */}
              <Route
                path="/actions"
                element={
                  <ActionsList
                    organisations={app.organisations}
                    modules={app.modules}
                    selectedOrg={app.selectedOrg}
                  />
                }
              />
              <Route
                path="/actions/:fieldName/:fieldValue"
                element={
                  <ActionsList
                    organisations={app.organisations}
                    modules={app.modules}
                  />
                }
              />
              <Route
                path="/actions/index/:fieldName/:fieldValue"
                element={
                  <ActionsList
                    organisations={app.organisations}
                    modules={app.modules}
                  />
                }
              />
              <Route
                path="/actions/view/:pageid"
                element={
                  <ActionsView
                    organisations={app.organisations}
                    modules={app.modules}
                  />
                }
              />
              <Route
                path="/actions/add"
                element={
                  <ActionsAdd
                    organisations={app.organisations}
                    modules={app.modules}
                  />
                }
              />
              <Route
                path="/actions/edit/:pageid"
                element={
                  <ActionsEdit
                    organisations={app.organisations}
                    modules={app.modules}
                  />
                }
              />
              {/* metrics pages routes */}
              <Route
                path="/metrics"
                element={
                  <MetricsList
                    organisations={app.organisations}
                    modules={app.modules}
                    selectedOrg={app.selectedOrg}
                  />
                }
              />
              <Route
                path="/metrics/:fieldName/:fieldValue"
                element={
                  <MetricsList
                    organisations={app.organisations}
                    modules={app.modules}
                  />
                }
              />
              <Route
                path="/metrics/index/:fieldName/:fieldValue"
                element={
                  <MetricsList
                    organisations={app.organisations}
                    modules={app.modules}
                  />
                }
              />
              <Route
                path="/metrics/view/:pageid"
                element={
                  <MetricsView
                    organisations={app.organisations}
                    modules={app.modules}
                  />
                }
              />
              <Route
                path="/metrics/add"
                element={
                  <MetricsAdd
                    organisations={app.organisations}
                    modules={app.modules}
                  />
                }
              />
              <Route
                path="/metrics/edit/:pageid"
                element={
                  <MetricsEdit
                    organisations={app.organisations}
                    modules={app.modules}
                  />
                }
              />
              
            </>
          )}
        </Route>
      </Route>
      <Route exact element={<IndexLayout />}>
        <Route
          path="/"
          element={
            !auth.isLoggedIn ? <IndexPage /> : <Navigate to="/home" replace />
          }
        />
        <Route path="/*" element={<IndexPages />} />
        <Route path="/error/*" element={<ErrorPages />} />
      </Route>
    </Routes>
  );
};
export default App;
